"use client";

import { useEffect, useState } from "react";
import { computeDashboardStats } from "@/lib/emailCleaner/compute";
import { formatBytes, formatCount } from "@/lib/emailCleaner/format";
import type { DashboardStats } from "@/lib/emailCleaner/types";
import type { FeatureKey } from "./featureKeys";

function healthLabel(score: number): string {
  if (score >= 80) return "Great";
  if (score >= 60) return "Good";
  if (score >= 40) return "Fair";
  return "Needs Attention";
}

type Feature = { key: FeatureKey; icon: string; title: string; description: string };

const SECTION_STYLES: Record<string, { icon: string; ring: string }> = {
  "Clean Up Your Inbox": {
    icon: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400",
    ring: "hover:border-emerald-300 dark:hover:border-emerald-500/50",
  },
  "Inbox & Organization": {
    icon: "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400",
    ring: "hover:border-blue-300 dark:hover:border-blue-500/50",
  },
  "AI Tools": {
    icon: "bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400",
    ring: "hover:border-violet-300 dark:hover:border-violet-500/50",
  },
  "Insights & Activity": {
    icon: "bg-pink-50 text-pink-600 dark:bg-pink-500/10 dark:text-pink-400",
    ring: "hover:border-pink-300 dark:hover:border-pink-500/50",
  },
};

const SECTIONS: { title: string; features: Feature[] }[] = [
  {
    title: "Clean Up Your Inbox",
    features: [
      { key: "explore", icon: "🧹", title: "Explore Cleaning Suggestions", description: "Real sender and category counts that are safe to archive or trash." },
      { key: "inbox-overview", icon: "📥", title: "View & Clean Your Inbox", description: "Recent senders grouped together — archive in bulk with one tap." },
      { key: "smart-clean", icon: "✨", title: "Smart Clean", description: "Old promotions, old emails, read newsletters and large attachments." },
      { key: "unsubscribe", icon: "🔕", title: "Unsubscribe from Mailing Lists", description: "Find newsletters and unsubscribe or mute them, with real counts." },
      { key: "block-mute", icon: "🚫", title: "Block or Mute a Sender", description: "Stop hearing from a specific address, instantly." },
      { key: "stop-unwanted", icon: "🛡️", title: "Stop All Unwanted Mail", description: "One tap to trash and block every unwanted source at once." },
      { key: "auto-organize", icon: "⚙️", title: "Auto-Organize", description: "One-tap presets: auto-archive read newsletters, auto-delete OTPs." },
    ],
  },
  {
    title: "Inbox & Organization",
    features: [
      { key: "inbox", icon: "📧", title: "Inbox", description: "Browse, search and run AI tools (summarize, reply, translate) on any email." },
      { key: "folders", icon: "📁", title: "Smart Folders", description: "18 pre-built folders like Shopping, Finance and Travel — or make your own." },
      { key: "senders", icon: "👥", title: "Sender Manager", description: "See your top senders and bulk delete, archive, mute or protect them." },
      { key: "automation-rules", icon: "🤖", title: "Automation Rules", description: "Describe a rule in plain English, or build one manually." },
    ],
  },
  {
    title: "AI Tools",
    features: [
      { key: "ai-assistant", icon: "💬", title: "AI Assistant", description: "Chat about your inbox — ask what needs attention today." },
      { key: "ai-summary", icon: "📝", title: "AI Summary", description: "A daily or weekly digest of what's in your inbox." },
      { key: "daily-brief", icon: "☀️", title: "AI Daily Brief", description: "Your inbox, summarized every morning, with top priorities." },
      { key: "ai-cleanup-coach", icon: "🧑‍🏫", title: "AI Cleanup Coach", description: "A friendlier walkthrough of what's safe to clean and why." },
      { key: "ai-insights", icon: "📊", title: "AI Insights", description: "Spending, subscriptions, habits and cleanup — all in one place." },
    ],
  },
  {
    title: "Insights & Activity",
    features: [
      { key: "subscriptions", icon: "🔔", title: "Subscriptions", description: "Every mailing list you're on, with real counts and AI-estimated frequency." },
      { key: "attachments", icon: "📎", title: "Attachments", description: "Browse, download or delete emails with attachments by file type." },
      { key: "analytics", icon: "📈", title: "Analytics", description: "Storage saved by category, tracked over time." },
      { key: "storage-analyzer", icon: "💾", title: "Storage Analyzer", description: "Your biggest senders and attachments, ranked by size." },
      { key: "productivity", icon: "⏱️", title: "Productivity", description: "Busiest day, peak hour, and your unread trend." },
      { key: "recent-activity", icon: "🕘", title: "Recent Activity", description: "A log of every cleanup you've run." },
    ],
  },
];

export default function Dashboard({
  accessToken,
  accountEmail,
  onOpen,
}: {
  accessToken: string;
  accountEmail: string;
  onOpen: (key: FeatureKey) => void;
}) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    computeDashboardStats(accessToken, accountEmail)
      .then(setStats)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load dashboard"));
  }, [accessToken, accountEmail]);

  return (
    <div>
      {error && (
        <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-600 dark:border-red-800 dark:bg-red-500/10 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="relative overflow-hidden rounded-2xl border border-violet-200/80 bg-gradient-to-br from-violet-50 via-fuchsia-50 to-pink-50 p-6 dark:border-violet-800/60 dark:from-violet-500/10 dark:via-fuchsia-500/10 dark:to-pink-500/10">
        <div className="flex items-center gap-5">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-white shadow-md shadow-violet-200/60 dark:bg-[#0f172a] dark:shadow-none">
            <span className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
              {stats ? stats.inboxHealthScore : "—"}
            </span>
          </div>
          <div>
            <p className="text-lg font-bold text-slate-900 dark:text-white">Inbox Health</p>
            <p className="text-sm font-medium text-violet-700 dark:text-violet-400">{stats ? healthLabel(stats.inboxHealthScore) : "Loading…"}</p>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile icon="📧" color="blue" label="Total Emails" value={stats ? formatCount(stats.totalEmails) : null} />
          <StatTile icon="🔵" color="violet" label="Unread" value={stats ? formatCount(stats.unreadEmails) : null} />
          <StatTile icon="💾" color="pink" label="Storage Used" value={stats ? formatBytes(stats.estimatedStorageUsedBytes) : null} />
          <StatTile icon="✅" color="emerald" label="Recoverable" value={stats ? formatBytes(stats.estimatedRecoverableBytes) : null} />
        </div>
      </div>

      {SECTIONS.map((section) => {
        const style = SECTION_STYLES[section.title];
        return (
          <div key={section.title} className="mt-8">
            <h3 className="mb-4 text-lg font-bold tracking-tight text-slate-900 dark:text-white">{section.title}</h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {section.features.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => onOpen(f.key)}
                  className={`group flex flex-col rounded-2xl border border-slate-200 bg-white/80 p-5 text-left backdrop-blur-sm transition hover:-translate-y-0.5 hover:shadow-lg dark:border-slate-800 dark:bg-[#0f172a]/80 ${style.ring}`}
                >
                  <div className={`flex h-10 w-10 items-center justify-center rounded-lg text-lg ${style.icon}`}>{f.icon}</div>
                  <h4 className="mt-4 font-semibold text-slate-900 dark:text-white">{f.title}</h4>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{f.description}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-slate-700 transition group-hover:gap-2 dark:text-slate-300">
                    Open <span aria-hidden="true">→</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const STAT_COLORS: Record<string, string> = {
  blue: "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400",
  violet: "bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400",
  pink: "bg-pink-50 text-pink-600 dark:bg-pink-500/10 dark:text-pink-400",
  emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400",
};

function StatTile({ icon, color, label, value }: { icon: string; color: string; label: string; value: string | null }) {
  return (
    <div className="rounded-xl bg-white/80 p-3 shadow-sm dark:bg-[#0f172a]/60">
      <div className={`flex h-7 w-7 items-center justify-center rounded-md text-sm ${STAT_COLORS[color]}`}>{icon}</div>
      <p className="mt-2 text-lg font-bold text-slate-900 dark:text-white">{value ?? "…"}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
    </div>
  );
}
